import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { cn } from "@/lib/utils";

export function AppointmentFormDialog({ open, onOpenChange, onBooked, presetPatientId, presetPatientName, presetLeadId }) {
  const [patients, setPatients] = useState([]);
  const [services, setServices] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [patientId, setPatientId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [doctorId, setDoctorId] = useState("");
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [notes, setNotes] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPatientId(presetPatientId || "");
    setServiceId(""); setDoctorId(""); setDate(""); setSlots([]); setSlot(""); setNotes("");
    api.get("/patients").then(({ data }) => setPatients(data.patients)).catch(() => {});
    api.get("/services", { params: { active_only: true } }).then(({ data }) => setServices(data.services)).catch(() => {});
    api.get("/doctors", { params: { active: "true" } }).then(({ data }) => setDoctors(data.doctors)).catch(() => {});
  }, [open, presetPatientId]);

  useEffect(() => {
    setSlot("");
    if (doctorId && serviceId && date) {
      setLoadingSlots(true);
      api.get(`/doctors/${doctorId}/slots`, { params: { date, service_id: serviceId } })
        .then(({ data }) => setSlots(data.slots))
        .catch(() => setSlots([]))
        .finally(() => setLoadingSlots(false));
    } else {
      setSlots([]);
    }
  }, [doctorId, serviceId, date]);

  const book = async () => {
    if (!patientId || !serviceId || !doctorId || !date || !slot) {
      toast.error("Please complete patient, service, doctor, date and slot.");
      return;
    }
    setBooking(true);
    try {
      await api.post("/appointments", {
        patient_id: patientId, service_id: serviceId, doctor_id: doctorId,
        date, start_time: slot, lead_id: presetLeadId || null, notes: notes || null,
      });
      toast.success("Appointment booked");
      onOpenChange(false);
      onBooked && onBooked();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Booking failed");
    } finally {
      setBooking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="appointment-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Book Appointment</DialogTitle>
          <DialogDescription>Select service, doctor and an available slot.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Patient</Label>
            {presetPatientId ? (
              <Input value={presetPatientName || "Selected patient"} disabled data-testid="appt-patient-fixed" />
            ) : (
              <Select value={patientId || undefined} onValueChange={setPatientId}>
                <SelectTrigger data-testid="appt-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
                <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.full_name} · {p.phone}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Service</Label>
              <Select value={serviceId || undefined} onValueChange={setServiceId}>
                <SelectTrigger data-testid="appt-service-select"><SelectValue placeholder="Select service" /></SelectTrigger>
                <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.duration}m)</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Doctor</Label>
              <Select value={doctorId || undefined} onValueChange={setDoctorId}>
                <SelectTrigger data-testid="appt-doctor-select"><SelectValue placeholder="Select doctor" /></SelectTrigger>
                <SelectContent>{doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="appt-date-input" />
          </div>

          <div className="space-y-1.5">
            <Label>Available slots</Label>
            {loadingSlots ? (
              <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Finding slots…</div>
            ) : !doctorId || !serviceId || !date ? (
              <p className="text-sm text-slate-400">Select service, doctor and date to see slots.</p>
            ) : slots.length === 0 ? (
              <p className="text-sm text-amber-600" data-testid="no-slots">No available slots for this day.</p>
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="slots-grid">
                {slots.map((s) => (
                  <button
                    key={s.start_time}
                    type="button"
                    onClick={() => setSlot(s.start_time)}
                    data-testid={`slot-${s.start_time}`}
                    className={cn(
                      "rounded-md border px-2 py-1.5 text-sm transition-colors",
                      slot === s.start_time
                        ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                        : "border-slate-200 text-slate-600 hover:border-emerald-300"
                    )}
                  >
                    {s.start_time}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" data-testid="appt-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={book} disabled={booking || !slot} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="appt-book-submit">
            {booking ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" />} Book
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
